import React from 'react';
import { SupportedLocales } from '@/providers/ConfigProvider/ConfigProvider';
import { Container, FlexContainer, TextLink } from '@cof/omni-react';
import { cn } from './cn';

const translations = {
  'en-US': {
    before: "You'll need your",
    beforeMany: "You'll need one of",
    after: 'for the next step.',
    cardEnding: 'card ending in',
    yourCards: 'your cards',
    or: 'or',
  },
  'es-US': {
    before: 'Necesitarás tu',
    beforeMany: 'Necesitarás una de',
    after: 'para el siguiente paso.',
    cardEnding: 'tarjeta que termina en',
    yourCards: 'tus tarjetas',
    or: 'o',
  },
};

function getCardInfo(product: [string, string]): {
  last4: string;
  name: string;
} {
  const value = product[1];
  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value);
    if (entries.length > 0) {
      return { last4: entries[0][0], name: entries[0][1] as string };
    }
  }
  return { last4: product[0], name: value };
}

function buildLinkText(
  cardProducts: [string, string][],
  locale: SupportedLocales,
): string {
  const t = translations[locale];
  const cards = cardProducts.map(getCardInfo);

  if (cards.length === 1) {
    return `${cards[0].name} ${t.cardEnding} ${cards[0].last4}`;
  }
  if (cards.length === 2) {
    return `${cards[0].name}...${cards[0].last4} ${t.or} ${cards[1].name}...${cards[1].last4}`;
  }
  if (cards.length === 3) {
    return `${cards[0].name}...${cards[0].last4}, ${cards[1].name}...${cards[1].last4} ${t.or} ${cards[2].name}...${cards[2].last4}`;
  }
  return t.yourCards;
}

function cardLink(
  locale: SupportedLocales,
  cardProducts: [string, string][],
  onShowCards?: () => void,
) {
  const t = translations[locale];
  const isMany = cardProducts.length > 3;
  const linkText = buildLinkText(cardProducts, locale);

  const before = isMany ? t.beforeMany : t.before;
  return (
    <Container>
      {before}{' '}
      <TextLink
        onClick={(e) => {
          e.preventDefault();
          onShowCards?.();
        }}
        aria-label={linkText}
      >
        {linkText}
      </TextLink>{' '}
      {t.after}
    </Container>
  );
}

const cardTextTranslations = {
  'en-US': {
    single: (cards: string[]) => (
      <>
        You'll need your <b>card ending in {cards[0]}</b> for the next step.
      </>
    ),
    double: (cards: string[]) => (
      <>
        You'll need your{' '}
        <b>
          card ending in {cards[0]} or {cards[1]}
        </b>{' '}
        for the next step.
      </>
    ),
    triple: (cards: string[]) => (
      <>
        You'll need your{' '}
        <b>
          card ending in {cards[0]}, {cards[1]} or {cards[2]}
        </b>{' '}
        for the next step.
      </>
    ),
  },
  'es-US': {
    single: (cards: string[]) => (
      <>
        Necesitarás tu tarjeta que termina en <b>{cards[0]}</b> para el
        siguiente paso.
      </>
    ),
    double: (cards: string[]) => (
      <>
        Necesitarás tu tarjeta que termina en <b>{cards[0]}</b> o{' '}
        <b>{cards[1]}</b> para el siguiente paso.
      </>
    ),
    triple: (cards: string[]) => (
      <>
        Necesitarás tu tarjeta que termina en <b>{cards[0]}</b>,{' '}
        <b>{cards[1]}</b> o <b>{cards[2]}</b> para el siguiente paso.
      </>
    ),
  },
};

export function getCardText(
  cards: string[],
  locale: SupportedLocales = 'en-US',
  showCardImage = false,
  cardProducts: [string, string][] = [],
  onShowCards?: () => void,
): React.ReactNode {
  // If showCardImage, dynamic card link for all cases
  if (showCardImage && cardProducts.length > 0) {
    return (
      <FlexContainer borderRadius="large" className={cn('bg-base2')}>
        <Container padding="medium">
          {cardLink(locale, cardProducts, onShowCards)}
        </Container>
        <img
          src={'/assets/tapArt.svg'}
          width={85}
          alt="tap"
          role="presentation"
        />
      </FlexContainer>
    );
  }

  // Original behavior when showCardImage is false
  const t = cardTextTranslations[locale];

  if (cards.length === 1) {
    return <Container>{t.single(cards)}</Container>;
  }
  if (cards.length === 2) {
    return <Container>{t.double(cards)}</Container>;
  }
  if (cards.length === 3) {
    return <Container>{t.triple(cards)}</Container>;
  }
  return <Container>{cardLink(locale, cardProducts, onShowCards)}</Container>;
}
